import {computed, inject, Injectable} from '@angular/core';
import {AddressDataDto, AddressInfoDto, RentalAssistanceType} from "homey-housing-lottery-app-dto";
import {from, Observable, switchMap, tap} from "rxjs";
import {UserService} from "../../../services/user.service";
import {ActivatedRoute, Router} from "@angular/router";
import {FlowDraftService} from "../../../services/flow-draft.service";
import {toSignal} from "@angular/core/rxjs-interop";
import {FormBuilder} from "@angular/forms";
import {atLeastOneRequiredValidator} from "../../../utils";
import {NewLotteriesAfterUserUpdateService} from "../../../services/new-lotteries-after-user-update.service";
import {AnalyticsService} from "../../../services/analytics.service";
import type {PostSignupUser} from "../../../model/user";

export type CompleteAddressInfoType = AddressInfoDto['type'];

export type AddressInfoFormData = Partial<{
  type: CompleteAddressInfoType,
  currentAddress: AddressDataDto,
  previousAddress: AddressDataDto,
  onNychaHousingForm: boolean,
  receivesRentalAssistance: boolean,
  rentalAssistanceTypes: RentalAssistanceType[],
  otherRentalAssistanceType: string,
}>

@Injectable({
  providedIn: 'root'
})
export class UpdateAddressInfoFormService {
  private readonly newLotteriesAfterUserUpdateService = inject(
    NewLotteriesAfterUserUpdateService
  );

  private readonly analyticsService = inject(
    AnalyticsService
  );

  public createPreviousAddress() {
    const initialUser = toSignal(this.userService.getCurrentUserAfterSignupFlow$());

    return computed(() => {
      const userAddressInfo = initialUser()?.addressInfo;

      if (userAddressInfo === undefined) {
        return undefined
      }

      return this.getAddressInfoData().previousAddress;
    });
  }

  private _addressInfoData: AddressInfoFormData | undefined;

  public waitUntilDataLoaded = async () => {
    if (this._addressInfoData === undefined) {
      this.analyticsService.sendEvent('address-flow-started');
      this.newLotteriesAfterUserUpdateService.storeOldData();
      this._addressInfoData = await this.flowDraftService.get('address-info')
        ?? this.initialFormDataFromUser();
    }
  }

  private initialFormDataFromUser(): AddressInfoFormData {
    const addressInfo = this.userService.userAfterSignupFlow()?.addressInfo;
    if (addressInfo === undefined || addressInfo.status !== 'complete') return {};

    const rentalAssistance = {
      receivesRentalAssistance: addressInfo.rentalAssistanceData.recievesRentalAssistance,
      rentalAssistanceTypes: [...addressInfo.rentalAssistanceData.rentalAssistanceTypes],
      otherRentalAssistanceType: addressInfo.rentalAssistanceData.otherRentalAssistanceType,
    };

    switch (addressInfo.type) {
      case 'regular':
        return {type: 'regular', currentAddress: addressInfo.currentAddress, ...rentalAssistance};
      case 'no-address':
        return {type: 'no-address', previousAddress: addressInfo.previousAddress, ...rentalAssistance};
      case 'shelter':
        return {type: 'shelter', currentAddress: addressInfo.currentAddress, previousAddress: addressInfo.previousAddress, ...rentalAssistance};
      case 'nycha':
        return {type: 'nycha', currentAddress: addressInfo.currentAddress, onNychaHousingForm: addressInfo.onForm, ...rentalAssistance};
    }
  }

  private getAddressInfoData(): AddressInfoFormData {
    if (this._addressInfoData === undefined)
      throw new Error('Address info data is not loaded');

    return this._addressInfoData;
  }

  public getIsNychaProperty(): boolean | undefined {
    const type = this._addressInfoData?.type;
    if (type === undefined) return undefined;
    return type === 'nycha';
  }

  public getIsHomeless(): boolean | undefined {
    const type = this._addressInfoData?.type;
    if (type === 'shelter' || type === 'no-address') return true;
    if (type === 'regular') return false;
    return undefined;
  }

  public getIsInShelter(): boolean | undefined {
    const type = this._addressInfoData?.type;
    if (type === 'shelter') return true;
    if (type === 'no-address') return false;
    return undefined;
  }

  public getOnNychaHousingForm(): boolean | undefined {
    return this._addressInfoData?.onNychaHousingForm;
  }

  public getReceivesRentalAssistance(): boolean | undefined {
    return this._addressInfoData?.receivesRentalAssistance;
  }

  private async updateAddressInfo(addressInfo: AddressInfoFormData) {
    this._addressInfoData = {...this.getAddressInfoData(), ...addressInfo};
    await this.flowDraftService.save('address-info', this._addressInfoData);
  }

  submitAddressType(type: CompleteAddressInfoType, activatedRoute: ActivatedRoute) {
    let route;
    switch (type) {
      case 'regular':
        route = ['..', 'current-address'];
        break;
      case 'no-address':
        route = ['..', 'previous-address'];
        break;
      case 'nycha':
        route = ['..', 'current-address'];
        break;
      case 'shelter':
        route = ['..', 'current-address'];
        break;
    }

    return from(this.updateAddressInfo({type: type})).pipe(
      switchMap(() => this.router.navigate(route, {relativeTo: activatedRoute}))
    );
  }

  createAddressInfo(): AddressInfoDto {
    const data = this.getAddressInfoData();

    if (!data.type) {
      throw new Error('Type is not set');
    }

    const rentalAssistanceTypes = data.rentalAssistanceTypes ?? [];

    switch (data.type) {
      case "regular":
        if (!data.currentAddress) {
          throw new Error('Current address is not set');
        }

        return {
          status: 'complete',
          type: data.type,
          currentAddress: data.currentAddress,
          rentalAssistanceData: {
            recievesRentalAssistance: rentalAssistanceTypes.length > 0 || data.otherRentalAssistanceType !== undefined,
            rentalAssistanceTypes: rentalAssistanceTypes,
            otherRentalAssistanceType: data.otherRentalAssistanceType,
          }
        }
      case "no-address":
        if (!data.previousAddress) {
          throw new Error('Previous address is not set');
        }

        return {
          status: 'complete',
          type: data.type,
          previousAddress: data.previousAddress,
          rentalAssistanceData: {
            recievesRentalAssistance: rentalAssistanceTypes.length > 0 || data.otherRentalAssistanceType !== undefined,
            rentalAssistanceTypes: rentalAssistanceTypes,
            otherRentalAssistanceType: data.otherRentalAssistanceType,
          }
        }
      case "shelter":
        if (!data.currentAddress) {
          throw new Error('Current address is not set');
        }
        if (!data.previousAddress) {
          throw new Error('Previous address is not set');
        }

        return {
          status: 'complete',
          type: data.type,
          currentAddress: data.currentAddress,
          previousAddress: data.previousAddress,
          rentalAssistanceData: {
            recievesRentalAssistance: rentalAssistanceTypes.length > 0 || data.otherRentalAssistanceType !== undefined,
            rentalAssistanceTypes: rentalAssistanceTypes,
            otherRentalAssistanceType: data.otherRentalAssistanceType,
          }
        }
      case "nycha":
        if (data.onNychaHousingForm === undefined) {
          throw new Error('On NYCHA housing form is not set');
        }
        if (!data.currentAddress) {
          throw new Error('Current address is not set');
        }

        return {
          status: 'complete',
          type: data.type,
          currentAddress: data.currentAddress,
          onForm: data.onNychaHousingForm,
          rentalAssistanceData: {
            recievesRentalAssistance: rentalAssistanceTypes.length > 0 || data.otherRentalAssistanceType !== undefined,
            rentalAssistanceTypes: rentalAssistanceTypes,
            otherRentalAssistanceType: data.otherRentalAssistanceType,
          }
        }
    }
  }

  complete(route: ActivatedRoute): Observable<unknown> {
    this.analyticsService.sendEvent('address-flow-success');
    return this.userService.updateAddressInfo(this.createAddressInfo()).pipe(
      tap(() => this.flowDraftService.delete('address-info')),
      tap(() => this._addressInfoData = undefined),
      switchMap(() => this.userService.getCurrentUserAfterSignupFlow$()),
      switchMap(() => this.router.navigate(['../..', 'complete', 'review'], {
        relativeTo: route,
      }))
    )
  }

  constructor(
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly flowDraftService: FlowDraftService,
    private readonly fb: FormBuilder
  ) {
  }


  submitPreviousAddressDetails(data: AddressDataDto, activatedRoute: ActivatedRoute) {
    return from(this.updateAddressInfo({previousAddress: data})).pipe(
      switchMap(() => this.router.navigate(['../..', '2'], {relativeTo: activatedRoute}))
    );
  }

  submitOnNychaHousingForm(value: "yes" | "no", route: ActivatedRoute) {
    return from(this.updateAddressInfo({onNychaHousingForm: value === 'yes'})).pipe(
      switchMap(() => this.router.navigate(['../..', '2'], {relativeTo: route}))
    );
  }

  private extractCurrentAddressData(user: AddressInfoDto): AddressDataDto | undefined {
    switch (user.type) {
      case "regular":
        return user.currentAddress;
      case "no-address":
        return undefined;
      case "shelter":
        return user.currentAddress;
      case "nycha":
        return user.currentAddress
    }
  }

  createCurrentAddressForm() {
    const initialUser = toSignal(this.userService.getCurrentUserAfterSignupFlow$());
    return computed(() => {
      const userAddressInfo = initialUser()?.addressInfo;

      let userAddressData: AddressDataDto | undefined;

      if (userAddressInfo !== undefined) {
        userAddressData = this.extractCurrentAddressData(userAddressInfo);
      }

      return this.getAddressInfoData().currentAddress ?? userAddressData;
    });
  }

  submitCurrentAddressDetails(data: AddressDataDto, route: ActivatedRoute) {
    const addressType = this.getAddressInfoData().type;

    return from(this.updateAddressInfo({currentAddress: data})).pipe(
      switchMap(() => {
        switch (addressType) {
          case 'shelter':
            return this.router.navigate(['..', 'previous-address'], {relativeTo: route});
          case 'nycha':
            return this.router.navigate(['..', 'nycha-form'], {relativeTo: route});
          default:
            return this.router.navigate(['../..', '2'], {relativeTo: route});
        }
      })
    );
  }

  submitRentalAssistanceTypes(rentalAssistanceTypes: RentalAssistanceType[], otherRentalAssistance: string, route: ActivatedRoute) {
    return from(this.updateAddressInfo({
      rentalAssistanceTypes: rentalAssistanceTypes,
      otherRentalAssistanceType: otherRentalAssistance
    })).pipe(
      switchMap(() => this.complete(route))
    );
  }

  submitReceivesRentalAssistance(value: 'yes' | 'no', route: ActivatedRoute) {
    return from(this.updateAddressInfo({receivesRentalAssistance: value === 'yes'})).pipe(
      switchMap(() => {
        if (value === 'yes') {
          return this.router.navigate(['..', 'rental-assistance-types'], {relativeTo: route});
        } else {
          return this.submitNoRentalAssistance(route);
        }
      })
    );
  }

  submitNoRentalAssistance(route: ActivatedRoute) {
    return from(this.updateAddressInfo({
      rentalAssistanceTypes: [],
      otherRentalAssistanceType: undefined,
    })).pipe(
      switchMap(() => this.complete(route))
    );
  }

  createRentalAssistanceForm() {
    const initialRentalAssistanceTypes = this.getAddressInfoData().rentalAssistanceTypes;
    const initialOtherRentalAssistanceType = this.getAddressInfoData().otherRentalAssistanceType;

    return this.fb.group({
      rentalAssistanceTypes: this.fb.nonNullable.control<RentalAssistanceType[]>(initialRentalAssistanceTypes ?? []),
      otherRentalAssistance: this.fb.nonNullable.control(initialOtherRentalAssistanceType ?? ''),
    }, {validators: [atLeastOneRequiredValidator]})
  }

  submitNYCHAProperty(value: "yes" | "no", route: ActivatedRoute) {
    if (value === 'yes') {
      return this.submitAddressType('nycha', route);
    } else {
      return this.router.navigate(['..', 'homeless'], {relativeTo: route});
    }
  }

  submitHomeless(value: "yes" | "no", route: ActivatedRoute) {
    if (value === 'yes') {
      return this.router.navigate(['..', 'homeless-shelter'], {relativeTo: route});
    } else {
      return this.submitAddressType('regular', route);
    }
  }

  submitShelter(value: "yes" | "no", route: ActivatedRoute) {
    if (value === 'yes') {
      return this.submitAddressType('shelter', route)
    } else {
      return this.submitAddressType('no-address', route);
    }
  }

}

type SectionStatus = 'not-started' | 'recommended' | 'complete' | 'problem';

export const getAddressStatus = (user: PostSignupUser): SectionStatus => {
  return user.addressInfo?.status === 'complete' ? 'complete' : 'recommended';
};

export const isAddressSectionComplete = (user?: PostSignupUser): boolean => {
  return !!user && getAddressStatus(user) === 'complete';
};
